import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { loadPdfDocument as fetchPdfDocument } from '../services/pdfLoader';
import { invalidatePresignedPdfUrl } from '../services/pdfPresignedUrl';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PdfViewerProps {
  pdfName: string;
  open: boolean;
  onClose: () => void;
  initialPage?: number;
  accessToken?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  pdfName, open, onClose, initialPage = 1, accessToken = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [rendering, setRendering] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;


  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      modalRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const cleanupPdf = useCallback(() => {
    renderTaskRef.current?.cancel();
    renderTaskRef.current = null;
    void pdfDocRef.current?.cleanup();
    pdfDocRef.current = null;
    void loadingTaskRef.current?.destroy();
    loadingTaskRef.current = null;
  }, []);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    const container = scrollRef.current;
    if (!pdf || !canvas || !container) return;

    renderTaskRef.current?.cancel();

    setRendering(true);
    try {
      const page = await pdf.getPage(pageNum);
      const containerWidth = container.clientWidth - 32;
      const unscaled = page.getViewport({ scale: 1 });
      const scale = Math.max(containerWidth / unscaled.width, 0.5);
      const viewport = page.getViewport({ scale });

      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const task = page.render({ canvasContext: context, viewport, canvas });
      renderTaskRef.current = task;
      await task.promise;
    } catch (err) {
      if ((err as { name?: string }).name !== 'RenderingCancelledException') {
        console.error('Error rendering PDF page:', err);
      }
    } finally {
      setRendering(false);
    }
  }, []);

  const loadPdfDocument = useCallback(async (forceRefresh = false) => {
    const token = accessTokenRef.current.trim();
    if (!token) {
      throw new Error('Please log in to view documents.');
    }

    return fetchPdfDocument(pdfName, token, forceRefresh);
  }, [pdfName]);

  useEffect(() => {
    if (!open || !pdfName) return;

    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      cleanupPdf();
      setNumPages(0);
      setCurrentPage(initialPage);

      try {
        let pdf: pdfjsLib.PDFDocumentProxy;
        try {
          pdf = await loadPdfDocument(false);
        } catch (firstError) {
          invalidatePresignedPdfUrl(pdfName);
          try {
            pdf = await loadPdfDocument(true);
          } catch {
            throw firstError;
          }
        }

        if (cancelled) {
          void pdf.cleanup();
          return;
        }

        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        const startPage = Math.min(Math.max(initialPage, 1), pdf.numPages);
        setCurrentPage(startPage);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      cleanupPdf();
    };
  }, [open, pdfName, initialPage, cleanupPdf, loadPdfDocument]);

  useEffect(() => {
    if (!loading && numPages > 0) {
      renderPage(currentPage);
    }
  }, [loading, numPages, currentPage, renderPage]);

  useEffect(() => {
    if (!open) return;

    const onResize = () => {
      if (numPages > 0) renderPage(currentPage);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, numPages, currentPage, renderPage]);

  const handleClose = () => {
    cleanupPdf();
    setError(null);
    setLoading(false);
    onClose();
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > numPages) return;
    setCurrentPage(page);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        ref={modalRef}
        className={`relative flex flex-col w-full mx-4 rounded-xl overflow-hidden shadow-2xl bg-slate-900 border border-slate-700/60 transition-all duration-200 ${isFullscreen ? 'max-w-none h-screen m-0 rounded-none border-0' : 'max-w-5xl h-[95vh]'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-red-500/15 border border-red-500/30 shrink-0">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-base font-semibold text-white truncate">
              PDF Document
            </h2>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!loading && !error && numPages > 0 && (
              <div className="flex items-center gap-1 mr-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1 || rendering}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-slate-300 tabular-nums min-w-[5rem] text-center">
                  {currentPage} / {numPages}
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= numPages || rendering}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            <button
              onClick={toggleFullscreen}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5M15 15l5.25 5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
              )}
            </button>

            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors"
              aria-label="Close PDF viewer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={scrollRef} className="relative flex-1 min-h-0 overflow-auto bg-slate-800/50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 rounded-full border-4 border-slate-700" />
                <div className="absolute inset-0 rounded-full border-4 border-t-red-400 animate-spin" />
              </div>
              <span className="text-sm text-slate-400 tracking-wide">Loading PDF…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-slate-300">{error}</p>
            </div>
          ) : (
            <div className="flex justify-center p-4">
              <canvas ref={canvasRef} className="shadow-lg max-w-full" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-700/60 shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 bg-slate-700/50 border border-slate-600/50 hover:bg-slate-600/60 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PdfViewer;
